import { assertOperation, canonicalSync, type OperationV2, SYNC_BATCH_BYTES, type SyncClock } from "@classroom/domain";
import type { CheckpointPointer } from "../../../../../supabase/functions/sync-control/model";
import {
  type CheckpointCell,
  type CheckpointManifest,
  type CheckpointSink,
  checkpointDue,
  checkpointManifest,
  readCheckpoint,
  writeCheckpoint,
} from "./checkpoints";
import {
  type CipherObject,
  canonical,
  type ObjectScope,
  signChallenge,
  unbase64url,
  verifyChallenge,
} from "./cryptoV2";
import { checkDriveCapacity, type DriveFileV2, type SyncDriveV2 } from "./driveV2";
import { openKeyHistory } from "./keyHistory";
import { packEncrypted, unpackEncrypted } from "./transportV2";

export type SyncRuntimeState = {
  datasetId: string;
  generation: string;
  deviceId: string;
  keyVersion: number;
  remoteCursor: string | null;
  clock: SyncClock;
  status: string;
};
export type SyncRuntimeStore = {
  state(): Promise<SyncRuntimeState | null>;
  pending(): Promise<OperationV2[]>;
  operation(id: string): Promise<{ operation: OperationV2; fileId: string | null; status: string } | null>;
  allocateFile(id: string, fileId: string): Promise<void>;
  acknowledge(expected: OperationV2, fileId: string): Promise<void>;
  receive(operations: OperationV2[]): Promise<unknown>;
  cursor(cursor: string, expected: SyncRuntimeState): Promise<void>;
  stage?(operations: OperationV2[]): Promise<void>;
  drain?(final: boolean): Promise<void>;
  checkpointPage?(after: string | null, clock: SyncClock): Promise<{ cells: CheckpointCell[]; after: string | null }>;
  checkpointSink?: CheckpointSink;
  checkpointAdvanceSink?(expected: SyncRuntimeState): CheckpointSink;
};
export type SyncRegistry = {
  root: Pick<SyncRuntimeState, "datasetId" | "generation" | "keyVersion"> & {
    checkpoint?: CheckpointPointer;
    keyHistory?: CipherObject;
  };
  devices: { id: string; signing: JsonWebKey; revokedAt?: number }[];
  revokedClocks?: SyncClock;
};
export type SyncRuntimeKeys = { signing: CryptoKey; key(version: number): Promise<CryptoKey | null> };
export type SyncProgress = {
  phase: "checking" | "checkpoint" | "listing" | "downloading" | "applying" | "uploading" | "finishing";
  completed: number;
  total?: number;
};
const scopeFor = (state: SyncRuntimeState, objectId: string, keyVersion = state.keyVersion): ObjectScope => ({
  datasetId: state.datasetId,
  generation: state.generation,
  keyVersion,
  objectId,
  format: 2,
});

async function sameGeneration(store: SyncRuntimeStore, expected: SyncRuntimeState) {
  const state = await store.state();
  if (
    !state ||
    state.status !== "active" ||
    state.datasetId !== expected.datasetId ||
    state.generation !== expected.generation
  )
    throw new Error("sync-generation-mismatch");
  return state;
}
export async function decryptOperation(
  cipher: CipherObject,
  state: SyncRuntimeState,
  registry: SyncRegistry,
  keys: SyncRuntimeKeys,
): Promise<OperationV2> {
  if (!cipher?.header || cipher.header.datasetId !== state.datasetId || cipher.header.generation !== state.generation)
    throw new Error("sync-generation-mismatch");
  const key = await keys.key(cipher.header.keyVersion);
  if (!key) throw new Error("sync-key-version-required");
  const payload = (await unpackEncrypted(
    key,
    scopeFor(state, cipher.header.objectId, cipher.header.keyVersion),
    cipher,
    SYNC_BATCH_BYTES,
  )) as { operation: OperationV2; signature: string };
  if (!payload || Object.keys(payload).sort().join() !== "operation,signature") throw new Error("sync-format");
  assertOperation(payload.operation);
  const op = payload.operation;
  if (op.id !== cipher.header.objectId || op.datasetId !== state.datasetId || op.generation !== state.generation)
    throw new Error("sync-generation-mismatch");
  const device = registry.devices.find((d) => d.id === op.deviceId);
  if (!device) throw new Error("sync-operation-signature");
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    device.signing,
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["verify"],
  );
  if (!(await verifyChallenge(publicKey, op, payload.signature))) throw new Error("sync-operation-signature");
  if (device.revokedAt && op.sequence > (registry.revokedClocks?.[device.id] ?? 0))
    throw new Error("sync-operation-revoked");
  return op;
}

export class SynchronizeV2 {
  private running: Promise<void> | null = null;
  private inventory = new Map<string, DriveFileV2>();
  private inventoryReady = false;
  private initialCursor: string | null = null;
  private previousCheckpoint?: CheckpointManifest;
  constructor(
    private readonly store: SyncRuntimeStore,
    private readonly drive: SyncDriveV2,
    private readonly registry: () => Promise<SyncRegistry>,
    private readonly keys: SyncRuntimeKeys,
    private readonly foreground: () => boolean = () => true,
    private readonly progress: (value: SyncProgress) => void = () => {},
  ) {}
  private assertForeground() {
    if (!this.foreground()) throw new Error("sync-paused");
  }
  private async refreshInventory() {
    let page: string | undefined;
    const seen = new Set<string>();
    do {
      this.assertForeground();
      const result = await this.drive.list(page);
      if (!Array.isArray(result.files)) throw new Error("sync-drive-response-format");
      for (const file of result.files) this.inventory.set(file.id, file);
      page = result.nextPageToken;
      if (page) {
        if (seen.has(page)) throw new Error("sync-drive-cursor");
        seen.add(page);
      }
    } while (page);
    this.inventoryReady = true;
  }
  async checkpoint(
    publish: (pointer: CheckpointPointer, expectedHash: string | null, state: SyncRuntimeState) => Promise<void>,
    force = false,
    keyOverride?: { version: number; key: CryptoKey },
  ) {
    const state = await this.store.state();
    if (
      !state ||
      state.status !== "active" ||
      !this.store.checkpointPage ||
      !this.inventoryReady ||
      (await this.store.pending()).length
    )
      throw new Error("sync-checkpoint-not-ready");
    const registry = await this.registry();
    if (
      registry.root.datasetId !== state.datasetId ||
      registry.root.generation !== state.generation ||
      registry.root.keyVersion !== state.keyVersion
    )
      throw new Error("sync-generation-mismatch");
    if (!force && !checkpointDue(registry.root.checkpoint, state.clock, Date.now())) return null;
    this.assertForeground();
    const key = keyOverride?.key ?? (await this.keys.key(state.keyVersion));
    if (!key) throw new Error("sync-locked");
    if (!keyOverride && !this.previousCheckpoint && registry.root.checkpoint)
      this.previousCheckpoint = await checkpointManifest(
        registry.root.checkpoint,
        this.drive,
        state,
        key,
        registry.devices,
      );
    const result = await writeCheckpoint(
      {
        clock: state.clock,
        page: async (after) => {
          this.assertForeground();
          return this.store.checkpointPage!(after, state.clock);
        },
      },
      this.drive,
      {
        datasetId: state.datasetId,
        generation: state.generation,
        keyVersion: keyOverride?.version ?? state.keyVersion,
      },
      key,
      this.keys.signing,
      state.deviceId,
      [...this.inventory.values()],
      keyOverride ? undefined : this.previousCheckpoint,
    );
    const latest = await sameGeneration(this.store, state);
    if (
      canonical(latest.clock) !== canonical(state.clock) ||
      latest.keyVersion !== state.keyVersion ||
      (await this.store.pending()).length
    )
      throw new Error("sync-checkpoint-stale");
    await publish(result.pointer, registry.root.checkpoint?.hash ?? null, state);
    this.previousCheckpoint = result.manifest;
    return result;
  }
  run() {
    if (!this.running)
      this.running = this.synchronize().finally(() => {
        this.running = null;
      });
    return this.running;
  }
  private async synchronize() {
    this.progress({ phase: "checking", completed: 0 });
    let state = await this.store.state();
    if (!state || state.status !== "active") throw new Error("sync-locked");
    const registry = await this.registry();
    if (registry.root.datasetId !== state.datasetId || registry.root.generation !== state.generation)
      throw new Error("sync-generation-mismatch");
    if (registry.root.keyVersion !== state.keyVersion) throw new Error("sync-key-version-required");
    const deviceId = state.deviceId;
    if (!registry.devices.some((d) => d.id === deviceId && !d.revokedAt)) throw new Error("sync-device-revoked");
    this.assertForeground();
    const epoch = { datasetId: state.datasetId, generation: state.generation, keyVersion: state.keyVersion };
    let historical: Promise<Map<number, CryptoKey>> | undefined;
    const keyRequests = new Map<number, Promise<CryptoKey | null>>();
    const runtimeKeys: SyncRuntimeKeys = {
      signing: this.keys.signing,
      key: async (version) => {
        const cached = keyRequests.get(version);
        if (cached) return cached;
        const request = (async () => {
          if (!Number.isSafeInteger(version) || version < 1 || version > epoch.keyVersion) return null;
          const direct = await this.keys.key(version);
          if (direct || version === epoch.keyVersion) return direct;
          historical ??= (async () => {
            const current = await this.keys.key(epoch.keyVersion);
            const result = new Map<number, CryptoKey>();
            if (!current) return result;
            for (const entry of await openKeyHistory(epoch, current, registry.root.keyHistory)) {
              const bytes = unbase64url(entry.material, 32);
              try {
                result.set(entry.version, await crypto.subtle.importKey("raw", bytes, "AES-GCM", false, ["decrypt"]));
              } finally {
                bytes.fill(0);
              }
            }
            return result;
          })();
          return (await historical).get(version) ?? null;
        })();
        keyRequests.set(version, request);
        return request;
      },
    };
    if (!Object.keys(state.clock).length && registry.root.checkpoint && this.store.checkpointSink) {
      this.progress({ phase: "checkpoint", completed: 0 });
      const key = await this.keys.key(state.keyVersion);
      if (!key) throw new Error("sync-locked");
      this.previousCheckpoint = await readCheckpoint(
        registry.root.checkpoint,
        this.drive,
        state,
        key,
        registry.devices,
        this.store.checkpointSink,
        (completed, total) => this.progress({ phase: "checkpoint", completed, total }),
      );
      state = await sameGeneration(this.store, state);
    }
    let start = state.remoteCursor ?? this.initialCursor;
    this.progress({ phase: "listing", completed: 0 });
    if (!this.inventoryReady) {
      // Capture the feed before listing so writes during the initial scan are
      // never skipped when the first cursor is committed.
      start ??= await this.drive.startChanges();
      // A pause or failed request may finish the inventory but not commit the
      // cursor. Retry from this same token so concurrent changes are not skipped.
      this.initialCursor = start;
      await this.refreshInventory();
    }
    if (!start) throw new Error("sync-drive-cursor");
    let cursor = start;
    let completed: string | undefined;
    const changed = new Map<string, DriveFileV2>();
    const seenTokens = new Set<string>();
    do {
      if (seenTokens.has(cursor)) throw new Error("sync-drive-cursor");
      seenTokens.add(cursor);
      const result = await this.drive.changes(cursor);
      if (!Array.isArray(result.changes)) throw new Error("sync-drive-response-format");
      for (const change of result.changes) {
        const known = this.inventory.get(change.fileId);
        // The changes feed also contains appDataFolder/legacy metadata, unlike
        // the filtered file listing. Only v2 files belong in this inventory.
        if (change.removed || change.file?.trashed || (change.file && change.file.appProperties?.cdSync !== "2")) {
          if (known?.appProperties?.dataset === state.datasetId && known.appProperties.generation === state.generation)
            throw new Error("sync-remote-file-removed");
          this.inventory.delete(change.fileId);
          changed.delete(change.fileId);
          continue;
        }
        if (change.file) {
          this.inventory.set(change.fileId, change.file);
          changed.set(change.fileId, change.file);
        }
      }
      if (result.nextPageToken) cursor = result.nextPageToken;
      else {
        completed = result.newStartPageToken;
        if (!completed) throw new Error("sync-drive-cursor");
        break;
      }
    } while (!completed);
    checkDriveCapacity([...this.inventory.values()]);
    const candidates = state.remoteCursor ? [...changed.values()] : [...this.inventory.values()];
    const receiveCandidates = async (files: DriveFileV2[]) => {
      const operations: OperationV2[] = [];
      const candidates = files.filter((file) => {
        const p = file.appProperties;
        return p?.dataset === state.datasetId && p.generation === state.generation && p.kind === "operation";
      });
      this.progress({ phase: "downloading", completed: 0, total: candidates.length });
      for (let offset = 0; offset < candidates.length; offset += 4) {
        this.assertForeground();
        // Bound memory/network use and settle every read before a failed run can
        // be retried. Keep original order and authenticate the whole batch before staging.
        const batch = await Promise.allSettled(
          candidates.slice(offset, offset + 4).map(async (file) => {
            // Feed entries can refer to a previously verified file that was later
            // changed. Always authenticate the current bytes; retaining all decoded
            // covered history here also defeats bounded checkpoint bootstrap memory.
            const cipher = await this.drive.read(file.id);
            if (!cipher) throw new Error("sync-remote-file-removed");
            let operation: OperationV2;
            try {
              operation = await decryptOperation(cipher, state, registry, runtimeKeys);
            } catch (error) {
              // Only a valid signature from a revoked source may be ignored past its
              // explicit cutoff. It must not revoke the receiving, still-valid device.
              if (error instanceof Error && error.message === "sync-operation-revoked") return null;
              throw error;
            }
            return operation;
          }),
        );
        for (const result of batch) if (result.status === "rejected") throw result.reason;
        this.assertForeground();
        // Clock coverage does not prove identical bytes or an identical operation
        // ID. Let SQLite check retained history before deduplicating the feed.
        for (const result of batch) if (result.status === "fulfilled" && result.value) operations.push(result.value);
        this.progress({
          phase: "downloading",
          completed: Math.min(offset + 4, candidates.length),
          total: candidates.length,
        });
        if (this.store.stage && this.store.drain && operations.length >= 32) {
          await sameGeneration(this.store, state);
          await this.store.stage(operations.splice(0));
          await this.store.drain(false);
        }
      }
      await sameGeneration(this.store, state);
      this.progress({ phase: "applying", completed: 0 });
      // A partially uploaded transaction keeps the cursor unchanged. Verified
      // staged operations remain in SQLite; no decoded history accumulates in RAM.
      if (this.store.stage && this.store.drain) {
        if (operations.length) await this.store.stage(operations);
        await this.store.drain(true);
      } else await this.store.receive(operations);
    };
    try {
      await receiveCandidates(candidates);
    } catch (error) {
      if (!(error instanceof Error) || error.message !== "sync-missing-parent") throw error;
      // A saved changes cursor is not proof that all causal history was applied.
      // Repair from retained, authenticated operation files once per run. If a
      // parent is still absent, keep staged data/pending writes and the old cursor.
      await sameGeneration(this.store, state);
      await this.refreshInventory();
      checkDriveCapacity([...this.inventory.values()]);
      await sameGeneration(this.store, state);
      try {
        await receiveCandidates([...this.inventory.values()]);
      } catch (missing) {
        if (!(missing instanceof Error) || missing.message !== "sync-missing-parent") throw missing;
        const current = await sameGeneration(this.store, state);
        const checkpoint = registry.root.checkpoint;
        // Retained history can be unavailable to a partially caught-up device.
        // Only a strictly newer, dominating signed checkpoint may advance it;
        // concurrent local work is never replaced to make a receive succeed.
        if (
          !checkpoint ||
          !this.store.checkpointAdvanceSink ||
          (await this.store.pending()).length ||
          Object.entries(current.clock).some(([device, seq]) => seq > (checkpoint.clock[device] ?? 0)) ||
          !Object.entries(checkpoint.clock).some(([device, seq]) => seq > (current.clock[device] ?? 0))
        )
          throw missing;
        const key = await this.keys.key(current.keyVersion);
        if (!key) throw new Error("sync-locked");
        const sink = this.store.checkpointAdvanceSink(current);
        this.previousCheckpoint = await readCheckpoint(checkpoint, this.drive, current, key, registry.devices, {
          begin: async (id, clock) => {
            this.assertForeground();
            await sink.begin(id, clock);
          },
          stage: async (id, cells) => {
            this.assertForeground();
            await sink.stage(id, cells);
          },
          commit: async (id) => {
            this.assertForeground();
            await sameGeneration(this.store, current);
            await sink.commit(id);
          },
          abort: (id) => sink.abort(id),
        });
        await receiveCandidates([...this.inventory.values()]);
      }
    }
    await this.store.cursor(completed, state);
    this.initialCursor = null;
    const pending = await this.store.pending();
    this.progress({ phase: "uploading", completed: 0, total: pending.length });
    for (const expected of pending) {
      this.assertForeground();
      await sameGeneration(this.store, state);
      const row = await this.store.operation(expected.id);
      if (!row || row.status !== "pending") continue;
      let fileId = row.fileId;
      if (!fileId) {
        fileId = await this.drive.allocateId();
        await this.store.allocateFile(expected.id, fileId);
      }
      let remote = await this.drive.read(fileId);
      if (!remote) {
        const key = await this.keys.key(state.keyVersion);
        if (!key) throw new Error("sync-locked");
        const cipher = await packEncrypted(
          key,
          scopeFor(state, expected.id),
          { operation: expected, signature: await signChallenge(this.keys.signing, expected) },
          SYNC_BATCH_BYTES,
        );
        checkDriveCapacity([...this.inventory.values()], new TextEncoder().encode(canonical(cipher)).length);
        try {
          await this.drive.create(fileId, cipher, "operation");
        } catch (error) {
          // Even an unknown POST outcome is resolved by reading the SAME ID.
          // Auth/quota/rate failures must keep their actionable state.
          if (!(error instanceof Error) || !["sync-drive-exists", "sync-drive-request-failed"].includes(error.message))
            throw error;
        }
        remote = await this.drive.read(fileId);
      }
      if (!remote) throw new Error("sync-upload-unconfirmed");
      const actual = await decryptOperation(remote, state, registry, this.keys);
      if (canonicalSync(actual) !== canonicalSync(expected)) throw new Error("sync-upload-mismatch");
      await sameGeneration(this.store, state);
      await this.store.acknowledge(expected, fileId);
      this.inventory.set(fileId, {
        id: fileId,
        size: String(new TextEncoder().encode(canonical(remote)).length),
        appProperties: {
          dataset: state.datasetId,
          generation: state.generation,
          kind: "operation",
          objectId: actual.id,
        },
      });
    }
    this.progress({ phase: "finishing", completed: 0 });
  }
}

export function retryDelay(attempt: number, random = Math.random) {
  return Math.min(60000, 1000 * 2 ** Math.min(6, Math.max(0, attempt))) * (0.75 + 0.25 * random());
}
